// tableRow.tsx
import './tableRow.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TableColumnConfig, JsonColumnConfig, RowActionConfig, SQLTableProps } from '../tableMain';
import TableCell from '../table_cell/tableCell';
import JsonCell from '../json_cell/jsonCell';
import Checkbox from '../../../components/common/checkbox/checkbox';
import RowActions from '../row_actions/rowActions';
import { getModifierKey } from '../../../helper_functions';
import { CellWarning } from '../dataProcessing';
import { config } from '../../../../config';

interface TableRowProps {
    tableType: SQLTableProps['tableType'];
    finalTableData: Record<string, any>[];
    tableName: string; // actual sql table name
    idColumn: string;
    columnConfigs: TableColumnConfig[];
    jsonColumnConfig?: JsonColumnConfig[];
    // utility parameters
    selectable: boolean; // if true, rows can be selected
    multipleSelection: boolean; // allows multiple rows to be selected
    onRowSelectionChange?: (selectedRows: number[], hasSelectedRows: boolean) => void; // callback for selection changes
    onUnselectAll?: () => void; // for external unselect all checkboxes
    onRowClick?: (rowData: any) => void; // callback when a row is clicked
    onSidepanelChange?: (data: { rowData: any; colName: string; tableName: string } | null) => void; // lift panel state to parent
    activePanelRowId?: any; // row id currently open in the parent-managed sidepanel (null when closed)
    isLoading?: boolean; // loading state for the table data
    // warning parameters
    warningsByRow?: Map<number, Map<string, CellWarning[]>>;
    ignoredActiveWarningsByRow?: Map<number, CellWarning[]>;
    // style parameters
    rowHeight: number;
    columnWidths: number[]; // Array of widths for each column
    totalWidth?: number; // Total width of the table
    minWidth: number; // Minimum width for each cell
    checkboxColumnWidth?: number; // Width for the checkbox column
    userRoleLevel?: number; // User role level for access control
    userData?: any; // User data for censored display
    rowRibbon?: {condition: (rowData: any) => boolean; color: string}; // Adds a small colored rectangle at row start
    rowColor?: Array<{condition: (rowData: any) => boolean; color: string}>; // Adds a background color to the row
    // row actions parameters
    rowActions?: RowActionConfig[]; // Array of action configurations
    rowActionsPosition?: 'start' | 'end'; // Where to render action column
    actionsColumnWidth?: number; // Width of actions column
    // side panel parameters
    activePanelCell?: { rowID: number; colName: string } | null;
    onPanelToggle?: (rowID: number, colName: string) => void;
    onPanelClose?: () => void;
}

const TableRow: React.FC<TableRowProps> = ({ 
    tableType,
    finalTableData,
    tableName,
    idColumn,
    columnConfigs,
    jsonColumnConfig,
    selectable,
    multipleSelection,
    onRowSelectionChange,
    onRowClick,
    onSidepanelChange,
    activePanelRowId,
    rowHeight,
    columnWidths,
    totalWidth,
    minWidth,
    checkboxColumnWidth,
    onUnselectAll,
    isLoading = false,
    userRoleLevel = 1, // default to viewer
    userData,
    warningsByRow,
    ignoredActiveWarningsByRow,
    rowRibbon,
    rowColor,
    rowActions,
    rowActionsPosition = 'end',
    actionsColumnWidth = 120,
    activePanelCell,
    onPanelToggle,
    onPanelClose,
}) => {
    const { currentEnv } = config;
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const [editingCell, setEditingCell] = useState<{ rowID: number; colName: string } | null>(null);
    const [isCtrlPressed, setIsCtrlPressed] = useState(false);
    const [hoveredRowId, setHoveredRowId] = useState<number | null>(null); // Add this

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Clear hover state when Ctrl is released
    useEffect(() => {
        if (!isCtrlPressed) {
            setHoveredRowId(null);
        }
    }, [isCtrlPressed]);
    
    // Reset modifier state when mouse leaves the component
    const handleMouseLeave = () => {
        setIsCtrlPressed(false);
    };

    // Re-check modifier state when mouse enters (in case it was held down while outside)
    const handleMouseEnter = (e: React.MouseEvent) => {
        if (getModifierKey(e) && selectable) {
            setIsCtrlPressed(true);
        }
    };

    // Function to find the matching JsonColumnConfig for a given column
    const matchJsonConfig = (colName: string): JsonColumnConfig | undefined => {
        return jsonColumnConfig?.find(jsonConfig => jsonConfig.pointsTo === colName);
    };

    // Function to unselect all rows
    const unselectAllRows = () => {
        setSelectedRows(new Set());
        onRowSelectionChange?.([], false);
    };
    
    // Expose unselect function to parent
    useEffect(() => {
        if (onUnselectAll) {
            (window as any).unselectAllRows = unselectAllRows;
        }
    }, [onUnselectAll]);
    
    // Handle checkbox selection
    const handleRowSelection = (rowID: number, checked: boolean, rowData?: any) => {

        if (currentEnv === 'dev') {
            console.log(rowData)
        }

        let newSelectedRows = new Set(selectedRows);
        
        if (multipleSelection) {
            // Multiple selection mode
            if (checked) {
                newSelectedRows.add(rowID);
            } else {
                newSelectedRows.delete(rowID);
            }
        } else if (!multipleSelection) {
            // Single selection mode
            if (checked) {
                // Clear all selections and add only this one
                newSelectedRows = new Set([rowID]);
            } else {
                // Remove this selection
                newSelectedRows.delete(rowID);
            }
        }
        
        setSelectedRows(newSelectedRows);
        const hasSelectedRows = newSelectedRows.size > 0;
        onRowSelectionChange?.(Array.from(newSelectedRows), hasSelectedRows);
    };

    // Create refs for all editable cells
    const editableCellRefs = useRef<Array<React.RefObject<HTMLInputElement>>>([]);

    // Helper: resolve editable to a per-row boolean (supports both boolean and function forms)
    const resolveEditable = (config: TableColumnConfig, rowData: any): boolean => {
        return typeof config.editable === 'function' ? config.editable(rowData) : !!config.editable;
    };

    // Build refs for each editable cell in finalTableData
    const editableCells: { row: number, col: number, rowID: any, colName: string, ref: React.RefObject<HTMLInputElement> }[] = [];
    finalTableData.forEach((rowData, rowIndex) => {
        columnConfigs.forEach((config, colIndex) => {
            if (resolveEditable(config, rowData)) {
                const ref = React.createRef<HTMLInputElement>();
                editableCells.push({
                    row: rowIndex,
                    col: colIndex,
                    rowID: rowData[idColumn],
                    colName: config.colName, 
                    ref
                });
            }
        });
    });
    editableCellRefs.current = editableCells.map(cell => cell.ref);

    // Function to render the appropriate cell component
    const renderCell = (rowData: any, config: TableColumnConfig, cellIndex: number, isFirstRow: boolean, isRowSelected: boolean, rowIndex: number) => {
        const cellWidth = {
            width: `${columnWidths[cellIndex]}px`,
            minWidth: `${minWidth}px`
        };
        const value = rowData[config.colName];
        const rowID = rowData[idColumn];

        // Check if this specific cell's panel is open
        const isPanelOpen = activePanelCell?.rowID === rowID && activePanelCell?.colName === config.colName;

        // Find the index of this cell in editableCells
        const isEditable = resolveEditable(config, rowData);
        let editableCellIndex = -1;
        if (isEditable) {
            editableCellIndex = editableCells.findIndex(
                cell => cell.row === rowIndex && cell.col === cellIndex
            );
        }
        
        // json cell rendering
        if (config.colType === 'json') {
            // Find the matching JsonColumnConfig for this column
            const matchingJsonConfig = matchJsonConfig(config.colName);
            return (
                <JsonCell
                    key={config.colName}
                    tableName={tableName}
                    columnConfigs={config}
                    idColumn={idColumn}
                    jsonColumnConfig={matchingJsonConfig}
                    rowData={rowData}
                    isSelected={isRowSelected}
                    style={{
                        ...cellWidth,
                        ...(isFirstRow && { borderTop: '1.5px solid var(--color-grey-border-light)' }),
                    }}
                />
            );
        }

        const cellWarnings = warningsByRow?.get(rowID)?.get(config.colName);
        const ignoredActiveWarnings = config.colName === idColumn
            ? ignoredActiveWarningsByRow?.get(rowID)
            : undefined;

        // default cell rendering
        return (
            <TableCell
                tableType={tableType}
                key={config.colName}
                rowData={rowData}
                tableName={tableName}
                colName={config.colName}
                actualColName={config.actualColName}
                idValue={rowID}
                value={value}
                cellConfig={config}
                isRowSelected={isRowSelected}
                cellWidth={cellWidth}
                isFirstRow={isFirstRow}
                inputRef={isEditable ? editableCellRefs.current[editableCellIndex] : undefined}
                editingCell={editingCell}
                setEditingCell={setEditingCell}
                userRoleLevel={userRoleLevel}
                userData={userData}
                cellWarnings={cellWarnings}
                ignoredActiveWarnings={ignoredActiveWarnings}
                // Panel props
                isPanelOpen={isPanelOpen}
                onPanelToggle={() => onPanelToggle?.(rowID, config.colName)}
                onPanelClose={onPanelClose}
                onTabToNextCell={(direction = 1) => { // direction 1 for next, -1 for previous
                    if (
                        editableCellIndex >= 0
                    ) {
                        const nextIndex = editableCellIndex + direction;
                        if (nextIndex >= 0 && nextIndex < editableCellRefs.current.length) {
                            const nextCell = editableCells[nextIndex];
                            if (nextCell) {
                                setEditingCell({ rowID: nextCell.rowID, colName: nextCell.colName });
                            }
                        }
                    }
                }}
            />
        );
    };
    
    return (
        <>
            <div 
                ref={scrollContainerRef}
                className="tableRow-mainContainer"
                onMouseLeave={handleMouseLeave}
                onMouseEnter={handleMouseEnter}
                style={{
                    height: '100%',
                    position: 'relative'
                }}
            >
                {isLoading ? (
                    // Skeleton loading state
                    <div>
                        {Array.from({ length: 5 }).map((_, skeletonIndex) => (
                            <div 
                                key={`skeleton-${skeletonIndex}`}
                                className="tableRow-skeletonRow"
                                style={{ 
                                    height: rowHeight,
                                    width: `${totalWidth}px`
                                }}
                            >
                                {selectable && (
                                    <div
                                        className='tableRow-checkboxContainer'
                                        style={{ width: `${checkboxColumnWidth}px`}}
                                    >
                                        <div className="tableRow-skeletonContent" style={{ width: '16px', height: '16px' }} />
                                    </div>
                                )}
                                {columnConfigs.map((config, cellIndex) => (
                                    <div
                                        key={`skeleton-${skeletonIndex}-${config.colName}`}
                                        className="tableRow-skeletonCell"
                                        style={{
                                            width: `${columnWidths[cellIndex]}px`,
                                            minWidth: `${minWidth}px`
                                        }}
                                    >
                                        <div className="tableRow-skeletonContent" />
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    // This div maintains the total scrollable height 
                    <div className="tableRow-mainContainer">
                        {finalTableData.map((rowData, rowIndex) => {
                            const isFirstRow = rowIndex === 0;
                            const rowID = rowData[idColumn];
                            const isRowSelected = selectedRows.has(rowID);
                            const isHoveringWithCtrl = isCtrlPressed && hoveredRowId === rowID;
                            
                            // Check if rowColor condition is met
                            let backgroundColor: string | undefined = undefined;
                            if (rowColor && rowColor.length > 0) {
                                // Find the first matching condition with highest priority
                                const matchingColor = rowColor.find(rc => rc.condition(rowData));
                                if (matchingColor) {
                                    backgroundColor = matchingColor.color;
                                }
                            }

                            // Create row props conditionally
                            const rowProps = {
                                className: `tableRow-rowContainer ${isRowSelected ? 'selected' : ''} ${isHoveringWithCtrl ? 'ctrl-hoverable' : ''}`,
                                style: {
                                    height: rowHeight,
                                    width: `${totalWidth}px`,
                                    ...(backgroundColor && { backgroundColor })
                                },
                                onClick: (e: React.MouseEvent) => {
                                    if (selectable && getModifierKey(e)) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleRowSelection(rowID, !isRowSelected, rowData);
                                    } else if (onRowClick && !selectable) {
                                        // Only trigger onRowClick if not in selection mode
                                        onRowClick(rowData);
                                    }
                                },
                                // Only add hover handlers when Ctrl is pressed
                                ...(isCtrlPressed && {
                                    onMouseEnter: () => setHoveredRowId(rowID),
                                    onMouseLeave: () => setHoveredRowId(null)
                                })
                            };

                            return (
                                <div key={rowID} {...rowProps} style={{ 
                                    ...rowProps.style, 
                                    position: 'relative' // Required for absolute positioning of ribbon
                                }}>
                                    {selectable && (
                                        <div
                                            className='tableRow-checkboxContainer'
                                            style={{ width: `${checkboxColumnWidth}px`}}
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevent the row click handler
                                                handleRowSelection(rowID, !isRowSelected);
                                            }}                        >
                                            <div className="tableRow-checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    size={16}
                                                    checked={isRowSelected}
                                                    onChange={(checked) => handleRowSelection(rowID, checked)}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {/* Row Ribbon - positioned absolutely to not take up space */}
                                    {rowRibbon && rowRibbon.condition(rowData) && (
                                        <div
                                            className="tableRow-rowRibbon"
                                            style={{ 
                                                backgroundColor: rowRibbon.color,
                                                left: selectable && checkboxColumnWidth ? `${checkboxColumnWidth}px` : '0'
                                            }}
                                        />
                                    )}
                                    {/* Render actions at start if configured */}
                                    {rowActions && rowActionsPosition === 'start' && (
                                        <RowActions
                                            rowData={rowData}
                                            rowId={rowID}
                                            actions={rowActions}
                                            userRoleLevel={userRoleLevel}
                                            cellWidth={{
                                                width: `${actionsColumnWidth}px`,
                                                minWidth: `${actionsColumnWidth}px`
                                            }}
                                            isFirstRow={isFirstRow}
                                            isRowSelected={isRowSelected}
                                        />
                                    )}
                                    {columnConfigs.map((config, cellIndex) => (
                                        <React.Fragment key={`${rowID}-${config.colName}`}>
                                            {renderCell(rowData, config, cellIndex, isFirstRow, isRowSelected, rowIndex)}
                                        </React.Fragment>
                                    ))}
                                    {/* Render actions at end if configured */}
                                    {rowActions && rowActionsPosition === 'end' && (
                                        <RowActions
                                            rowData={rowData}
                                            rowId={rowID}
                                            actions={rowActions}
                                            userRoleLevel={userRoleLevel}
                                            cellWidth={{
                                                width: `${actionsColumnWidth}px`,
                                                minWidth: `${actionsColumnWidth}px`
                                            }}
                                            isFirstRow={isFirstRow}
                                            isRowSelected={isRowSelected}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
};

export default TableRow;